/**
 * User Management Redux Slice
 * State management for User Management module
 * API calls are handled directly in components via apiRoute.ts
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import {
  User,
  FilterState,
  SortConfig,
  DEFAULT_FILTERS,
  DEFAULT_SORT,
  TrainingType,
} from '../../screens/Admin/pages/UserManagement/userManagementTypes';

// ─── State Interface ──────────────────────────────────────────────────────────
interface UserManagementState {
  users: User[];
  filteredUsers: User[];
  selectedUserIds: string[];
  filters: FilterState;
  sortConfig: SortConfig;
  selectedUser: User | null;
  hasUnsavedChanges: boolean;
  loading: boolean;      // True while fetchUsers is in-flight (shows skeleton/loading)
  isSaving: boolean;     // True while updateUser/bulk operations are in-flight
  error: string | null;
  // Pagination
  currentPage: number;
  totalCount: number;
  totalPages: number;
  pageSize: number;
  // Dashboard card stats — isolated from state.users so table page navigation
  // never overwrites these counts.
  dashboardStats: {
    activeCount: number;
    newCount: number;
    loaded: boolean;
  };
}
//merge into develop
// ─── Initial State ────────────────────────────────────────────────────────────
const initialState: UserManagementState = {
  users: [],
  filteredUsers: [],
  selectedUserIds: [],
  filters: DEFAULT_FILTERS,
  sortConfig: DEFAULT_SORT,
  selectedUser: null,
  hasUnsavedChanges: false,
  loading: false,
  isSaving: false,
  error: null,
  currentPage: 0,
  totalCount: 0,
  totalPages: 0,
  pageSize: parseInt(process.env.USER_MGMT_PAGE_SIZE ?? '10', 10) || 10,
  dashboardStats: {
    activeCount: 0,
    newCount: 0,
    loaded: false,
  },
};

// ─── Slice ────────────────────────────────────────────────────────────────────
const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    // ─── Set Users ──────────────────────────────────────────────────────────
    // Stores all records from the backend as-is. The backend may return the
    // same user ID multiple times when a user has multiple role/location
    // assignments — each row must be displayed.
    setUsers: (state, action: PayloadAction<User[]>) => {
      state.users = action.payload;
      state.filteredUsers = action.payload;
    },

    // ─── Append Users (infinite scroll) ────────────────────────────────────
    // Merges a new page of users into the existing list, skipping duplicates.
    appendUsers: (state, action: PayloadAction<User[]>) => {
      const existingIds = new Set(state.users.map(u => u.id));
      const newUsers = action.payload.filter(u => !existingIds.has(u.id));
      state.users = [...state.users, ...newUsers];
    },

    // ─── Reset Users (e.g. on search change) ───────────────────────────────
    resetUsers: (state) => {
      state.users = [];
      state.filteredUsers = [];
    },

    // ─── Set Filtered Users ─────────────────────────────────────────────────
    setFilteredUsers: (state, action: PayloadAction<User[]>) => {
      state.filteredUsers = action.payload;
    },

    // ─── Selection Management ───────────────────────────────────────────────
    toggleUserSelection: (state, action: PayloadAction<string>) => {
      const userId = action.payload;
      const index = state.selectedUserIds.indexOf(userId);
      if (index > -1) {
        state.selectedUserIds.splice(index, 1);
      } else {
        state.selectedUserIds.push(userId);
      }
    },

    selectAllUsers: (state) => {
      state.selectedUserIds = state.filteredUsers.map(u => u.id);
    },

    clearSelection: (state) => {
      state.selectedUserIds = [];
    },

    // ─── Filter Management ──────────────────────────────────────────────────
    setFilters: (state, action: PayloadAction<Partial<FilterState>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 0; // Server-side: reset to first page on filter change
      state.selectedUserIds = [];
    },

    clearFilters: (state) => {
      state.filters = DEFAULT_FILTERS;
      state.currentPage = 0;
      state.selectedUserIds = [];
    },

    // ─── Sort Management ────────────────────────────────────────────────────
    setSortConfig: (state, action: PayloadAction<SortConfig>) => {
      state.sortConfig = action.payload;
      state.currentPage = 0; // Server-side: reset to first page on sort change
      state.selectedUserIds = [];
    },

    resetSortConfig: (state) => {
      state.sortConfig = DEFAULT_SORT;
    },

    // ─── User Detail Management ─────────────────────────────────────────────
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload;
      state.hasUnsavedChanges = false;
    },

    updateSelectedUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.selectedUser) {
        state.selectedUser = { ...state.selectedUser, ...action.payload };
        state.hasUnsavedChanges = true;
      }
    },

    saveSelectedUser: (state) => {
      if (state.selectedUser) {
        const index = state.users.findIndex(u => u.id === state.selectedUser!.id);
        if (index > -1) {
          state.users[index] = state.selectedUser;
        }
        const filteredIndex = state.filteredUsers.findIndex(u => u.id === state.selectedUser!.id);
        if (filteredIndex > -1) {
          state.filteredUsers[filteredIndex] = state.selectedUser;
        }
        state.hasUnsavedChanges = false;
      }
    },

    // Explicitly patch a user in both arrays using a confirmed payload (e.g.
    // the object returned by the backend after update). Use this instead of
    // saveSelectedUser when you have the authoritative updated user object.
    patchUserInList: (state, action: PayloadAction<User>) => {
      const updated = action.payload;
      const index = state.users.findIndex(u => u.id === updated.id);
      if (index > -1) {
        state.users[index] = updated;
      }
      const filteredIndex = state.filteredUsers.findIndex(u => u.id === updated.id);
      if (filteredIndex > -1) {
        state.filteredUsers[filteredIndex] = updated;
      }
      // Keep selectedUser in sync if it's the same record
      if (state.selectedUser?.id === updated.id) {
        state.selectedUser = updated;
      }
      state.hasUnsavedChanges = false;
    },

    discardChanges: (state) => {
      if (state.selectedUser) {
        const original = state.users.find(u => u.id === state.selectedUser!.id);
        if (original) {
          state.selectedUser = { ...original };
        }
      }
      state.hasUnsavedChanges = false;
    },

    // ─── Bulk Training Date Update ──────────────────────────────────────────
    setBulkTrainingDate: (
      state,
      action: PayloadAction<{ trainingType: TrainingType; date: string | null }>
    ) => {
      const { trainingType, date } = action.payload;
      const field = trainingType === 'fundamentals' 
        ? 'fundamentals' 
        : trainingType === 'lighthouse' 
        ? 'lighthouse' 
        : 'advFundamentals';

      state.selectedUserIds.forEach(userId => {
        const user = state.users.find(u => u.id === userId);
        if (user) {
          user[field] = date;
        }
        // filteredUsers is what the table renders — must stay in sync
        const filteredUser = state.filteredUsers.find(u => u.id === userId);
        if (filteredUser) {
          filteredUser[field] = date;
        }
      });
      
      // Clear selection after bulk action
      state.selectedUserIds = [];
    },

    // ─── Clear New Tags ─────────────────────────────────────────────────────
    clearNewTags: (state, action: PayloadAction<string[]>) => {
      const userIds = action.payload;
      userIds.forEach(userId => {
        const user = state.users.find(u => u.id === userId);
        if (user) {
          user.newStatus = false;
        }
        // filteredUsers is what the table renders — must stay in sync
        const filteredUser = state.filteredUsers.find(u => u.id === userId);
        if (filteredUser) {
          filteredUser.newStatus = false;
        }
      });
      
      // Clear selection if bulk action
      if (userIds.length > 1) {
        state.selectedUserIds = [];
      }
    },

    // ─── Loading & Error ────────────────────────────────────────────────────
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // ─── Pagination ─────────────────────────────────────────────────────────
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
      state.selectedUserIds = [];
    },

    setPaginationMeta: (state, action: PayloadAction<{ totalCount: number; totalPages: number }>) => {
      state.totalCount = action.payload.totalCount;
      state.totalPages = action.payload.totalPages;
    },

    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 0;      // Reset to first page on page size change
      state.selectedUserIds = [];
    },

    // ─── Remove User From List ──────────────────────────────────────────────
    removeUserFromList: (state, action: PayloadAction<string>) => {
      const userId = action.payload;
      state.users = state.users.filter(u => u.id !== userId);
      state.filteredUsers = state.filteredUsers.filter(u => u.id !== userId);
      state.selectedUserIds = state.selectedUserIds.filter(id => id !== userId);
      if (state.totalCount > 0) state.totalCount -= 1;
      state.selectedUser = null;
    },

    // Written only by AdminDashboard on mount; never touched by table pagination.
    setDashboardStats: (state, action: PayloadAction<{ activeCount: number; newCount: number }>) => {
      state.dashboardStats.activeCount = action.payload.activeCount;
      state.dashboardStats.newCount = action.payload.newCount;
      state.dashboardStats.loaded = true;
    },
  },
});

// ─── Actions ──────────────────────────────────────────────────────────────────
export const {
  setUsers,
  appendUsers,
  resetUsers,
  setFilteredUsers,
  toggleUserSelection,
  selectAllUsers,
  clearSelection,
  setFilters,
  clearFilters,
  setSortConfig,
  resetSortConfig,
  setSelectedUser,
  updateSelectedUser,
  saveSelectedUser,
  patchUserInList,
  removeUserFromList,
  discardChanges,
  setBulkTrainingDate,
  clearNewTags,
  setLoading,
  setError,
  setCurrentPage,
  setPaginationMeta,
  setPageSize,
  setDashboardStats,
} = userManagementSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────────────────
export const selectUsers = (state: RootState) => state.userManagement.users;
export const selectFilteredUsers = (state: RootState) => state.userManagement.filteredUsers;
export const selectSelectedUserIds = (state: RootState) => state.userManagement.selectedUserIds;
export const selectFilters = (state: RootState) => state.userManagement.filters;
export const selectSortConfig = (state: RootState) => state.userManagement.sortConfig;
export const selectSelectedUser = (state: RootState) => state.userManagement.selectedUser;
export const selectHasUnsavedChanges = (state: RootState) => state.userManagement.hasUnsavedChanges;
export const selectLoading = (state: RootState) => state.userManagement.loading;
export const selectIsSaving = (state: RootState) => state.userManagement.isSaving;
export const selectError = (state: RootState) => state.userManagement.error;

// ─── Computed Selectors ───────────────────────────────────────────────────────
export const selectNewUsersCount = (state: RootState) =>
  state.userManagement.users.filter((u: User) => u.newStatus).length;

export const selectActiveUsersCount = (state: RootState) =>
  state.userManagement.users.filter((u: User) => u.activeStatus === 'Active').length;

export const selectInactiveUsersCount = (state: RootState) =>
  state.userManagement.users.filter((u: User) => u.activeStatus === 'Inactive').length;

export const selectCurrentPage = (state: RootState) => state.userManagement.currentPage;
export const selectTotalCount = (state: RootState) => state.userManagement.totalCount;
export const selectTotalPages = (state: RootState) => state.userManagement.totalPages;
export const selectPageSize = (state: RootState) => state.userManagement.pageSize;
export const selectDashboardStats = (state: RootState) => state.userManagement.dashboardStats;

export default userManagementSlice.reducer;
